// src/components/gamificationengine/GamificationEngine.js - FIXED VERSION
import { getMyGamificationProfile, updateMyProfile } from '../../api/user.api';
import { submitGameResults } from '../../api/games.api';


class GamificationEngine {
  constructor(userId, initialUserData = null) {
    this.userId = userId;
    this.listeners = {};
    this.activeActivities = {};
    this.pendingSync = [];
    
    // Initialize user profile with sensible defaults
    this.userProfile = this.createDefaultProfile();
    
    // Override defaults with any provided initial data
    if (initialUserData) {
      this.userProfile = { ...this.userProfile, ...initialUserData };
    }
    
    // Auto-sync with backend periodically
    this.startAutoSync();
  }

  createDefaultProfile() {
    return {
      userId: this.userId,
      level: 1,
      xp: 0,
      totalXp: 0,
      badges: [],
      skills: {
        'social-engineering': 0,
        'network-security': 0,
        'malware-defense': 0,
        'authentication': 0,
        'data-protection': 0,
        'incident-response': 0,
        'mobile-security': 0
      },
      achievements: [],
      stats: {
        challengesCompleted: 0,
        currentStreak: 0,
        longestStreak: 0,
        totalPlayTime: 0,
        averageScore: 0
      },
      lastSyncTime: new Date().toISOString()
    };
  }

  // Start automatic syncing with backend
  startAutoSync() {
    // Sync every 5 minutes
    this.syncInterval = setInterval(() => {
      this.synchronizeWithBackend();
    }, 5 * 60 * 1000);

    // Initial sync
    setTimeout(() => {
      this.synchronizeWithBackend();
    }, 1000);
  }

  // Stop automatic syncing
  stopAutoSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }
  }

  // Record when an activity/challenge starts
  recordActivityStart(activityId, metadata = {}) {
    this.activeActivities[activityId] = {
      startTime: Date.now(),
      activityId,
      metadata
    };
    
    // console.log(`🎮 Activity started: ${activityId}`, metadata);
    this.emit("activity-started", { activityId, metadata });
    return this.activeActivities[activityId];
  }

  // Record when an activity/challenge completes
  async recordActivityComplete(activityId, result = {}) {
    const activity = this.activeActivities[activityId];
    if (!activity) {
      console.warn(`No active activity found for ${activityId}`);
      return null;
    }

    const endTime = Date.now();
    const durationMs = endTime - activity.startTime;
    // **FIXED**: Only award XP if user got correct answers (score > 0)
    const xpAwarded = (result.score > 0) ? (result.xpValue || result.earnedXp || 0) : 0;

    // Update local profile
    this.awardXP(xpAwarded, `Completed ${activityId}`);
    this.updateStats(result);

    // Prepare completion data
    const completionData = { 
      activityId, 
      durationMs, 
      score: result.score || 0, 
      xpAwarded 
    };

    // Add to pending sync queue for backend submission
    this.pendingSync.push({
      type: 'activity_completion',
      activityId,
      data: {
        challengeId: activityId,
        gameType: result.gameType || 'simulation',
        score: result.score || 0,
        timeSpent: Math.round(durationMs / 1000),
        earnedXp: xpAwarded,
        completionData: {
          ...result,
          ...activity.metadata,
          completion_timestamp: new Date().toISOString()
        }
      }
    });

    // Try immediate sync
    this.syncPendingData();

    this.emit("activity-completed", completionData);
    delete this.activeActivities[activityId];
    
    // console.log(`✅ Activity completed: ${activityId}`, completionData);
    return completionData;
  }

  // Award XP and handle level ups
  awardXP(points, reason) {
    const oldLevel = this.userProfile.level;
    this.userProfile.xp += points;
    this.userProfile.totalXp += points;
    
    // Check for level up (100 XP per level, exponential scaling)
    const newLevel = Math.floor(Math.sqrt(this.userProfile.totalXp / 100)) + 1;
    
    if (newLevel > oldLevel) {
      this.userProfile.level = newLevel;
      this.emit("level-up", { 
        oldLevel, 
        newLevel, 
        totalXp: this.userProfile.totalXp 
      });
      // console.log(`🎉 Level up! ${oldLevel} → ${newLevel}`);
    }
    
    this.emit("xp-gained", { 
      points, 
      reason, 
      totalXp: this.userProfile.totalXp,
      level: this.userProfile.level
    });
    
    return { 
      xp: this.userProfile.xp, 
      totalXp: this.userProfile.totalXp, 
      level: this.userProfile.level,
      reason 
    };
  }

  // Update user statistics
  updateStats(result) {
    if (result.score !== undefined) {
      const currentAvg = this.userProfile.stats.averageScore || 0;
      const completed = this.userProfile.stats.challengesCompleted || 0;
      
      // Calculate new average
      this.userProfile.stats.averageScore = 
        Math.round(((currentAvg * completed) + result.score) / (completed + 1));
      
      this.userProfile.stats.challengesCompleted = completed + 1;
    }

    if (result.timeSpent) {
      this.userProfile.stats.totalPlayTime = 
        (this.userProfile.stats.totalPlayTime || 0) + result.timeSpent;
    }
  }

  // Event system
  on(event, callback) {
    if (!this.listeners[event]) this.listeners[event] = [];
    this.listeners[event].push(callback);
  }
  
  off(event, callback) {
    if (!this.listeners[event]) return;
    this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
  }

  emit(event, data) {
    if (!this.listeners[event]) return;
    this.listeners[event].forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in event listener for ${event}:`, error);
      }
    });
  }

  // Synchronize with backend
  async synchronizeWithBackend() {
    try {
      // console.log('🔄 Syncing gamification data with backend...');
      
      const gameData = await getMyGamificationProfile();
      
      // Update local state with backend data
      if (gameData) {
        // console.log('✅ Fetched gamification data from backend:', gameData);
        
        this.userProfile.level = gameData.level || this.userProfile.level;
        this.userProfile.xp = gameData.xp || this.userProfile.xp;
        this.userProfile.totalXp = gameData.totalXp || this.userProfile.totalXp;
        this.userProfile.badges = gameData.badges || this.userProfile.badges;
        this.userProfile.skills = { ...this.userProfile.skills, ...(gameData.skills || {}) };
        this.userProfile.achievements = gameData.achievements || this.userProfile.achievements;
        this.userProfile.stats.currentStreak = gameData.streak || this.userProfile.stats.currentStreak;
        this.userProfile.lastSyncTime = new Date().toISOString();

        // console.log('✅ Gamification data synced from backend:', this.userProfile);
      }
      
      return true;
    } catch (error) {
      console.error("❌ Failed to sync with backend:", error);
      return false;
    }
  }

  // Sync pending data to backend
  async syncPendingData() {
    if (this.pendingSync.length === 0) return;

    const itemsToSync = [...this.pendingSync];
    this.pendingSync = []; // Clear the queue

    for (const item of itemsToSync) {
      try {
        if (item.type === 'activity_completion') {
          await submitGameResults(item.data);
          // console.log('✅ Synced activity completion to backend:', item.activityId);
        }
      } catch (error) {
        console.error('❌ Failed to sync item to backend:', error);
        // Re-add failed items to queue for retry
        this.pendingSync.push(item);
      }
    }
  }

  // Load profile from backend
  async loadProfile() {
    try {
      console.log('🔄 Loading gamification profile...');
      await this.synchronizeWithBackend();
      return this.userProfile;
    } catch (error) {
      console.error("❌ Error loading gamification profile:", error);
      return this.userProfile || this.createDefaultProfile();
    }
  }

  // Save to backend
  async saveToBackend() {
    try {
      console.log('💾 Saving gamification profile to backend...');
      
      // Sync any pending data first
      await this.syncPendingData();
      
      // Update user profile with gamification data
      await updateMyProfile({ 
        gamification: this.userProfile,
        lastUpdated: new Date().toISOString()
      });
      
      console.log('✅ Gamification profile saved to backend');
      return true;
    } catch (error) {
      console.error("❌ Failed to save to backend:", error);
      return false;
    }
  }

  // Get current profile
  getProfile() {
    return { ...this.userProfile };
  }

  // Get specific stats
  getStats() {
    return { ...this.userProfile.stats };
  }

  // Get skills
  getSkills() {
    return { ...this.userProfile.skills };
  }

  // Clean up resources
  destroy() {
    this.stopAutoSync();
    this.listeners = {};
    this.activeActivities = {};
    this.pendingSync = [];
  }
}

export default GamificationEngine;